import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep as _cloneDeep } from 'lodash';

import SDK from '../Components/SDK';
import logger from '../Helpers/logger';
import { clearState } from './clearSlice';

export const QUOTE_STATUS = {
    LOADING: 'loading',
    READY: 'ready',
    UNAVAILABLE: 'unavailable',
    FAILED: 'failed',
};

// Reply targets are keyed by conversation, so a reply left open in one chat never
// turns another chat into reply mode. Quotes are keyed by the quoted message id.
const initialState = {
    targets: {},
    quotes: {},
};

const replySlice = createSlice({
    name: 'reply',
    initialState,
    reducers: {
        startReply(state, action) {
            const { chatId, message } = action.payload || {};
            if (!chatId || !message?.msgId) return;
            // Immer freezes what it stores, and the chat history still mutates its own messages.
            state.targets[chatId] = _cloneDeep(message);
        },
        clearReply(state, action) {
            delete state.targets[action.payload];
        },
        dropReplyTargets(state, action) {
            const msgIds = action.payload || [];
            Object.keys(state.targets).forEach(chatId => {
                if (msgIds.includes(state.targets[chatId].msgId)) delete state.targets[chatId];
            });
        },
        setQuoteStatus(state, action) {
            const { msgId, status, data = null } = action.payload || {};
            if (!msgId) return;
            state.quotes[msgId] = { ...state.quotes[msgId], status, data };
        },
        markQuoteUnreachable(state, action) {
            const msgId = action.payload;
            if (!msgId) return;
            state.quotes[msgId] = {
                status: QUOTE_STATUS.READY,
                data: null,
                ...state.quotes[msgId],
                isUnreachable: true,
            };
        },
    },
    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
    },
});

export const { startReply, clearReply, dropReplyTargets, setQuoteStatus, markQuoteUnreachable } = replySlice.actions;

export const selectReplyTarget = (state, chatId) => state.reply.targets[chatId] || null;

export const selectIsReplying = (state, chatId) => Boolean(state.reply.targets[chatId]);

export const selectQuote = (state, msgId) => state.reply.quotes[msgId] || null;

/**
 * Loads the message a reply quotes when it is not in the loaded history. Every bubble
 * quoting the same message shares one request; a failed one is retried on the next call.
 *
 * @param {string} msgId Id of the quoted message.
 * @param {string} chatType Chat type of the conversation.
 */
export const requestReplyQuote = (msgId, chatType) => async (dispatch, getState) => {
    const current = selectQuote(getState(), msgId);
    if (!msgId || (current && current.status !== QUOTE_STATUS.FAILED)) return;

    dispatch(setQuoteStatus({ msgId, status: QUOTE_STATUS.LOADING }));
    const response = await SDK.getReplyMessage(msgId, chatType);
    if (response?.statusCode !== 200) {
        logger.warn('Quoted message not loaded', { msgId, statusCode: response?.statusCode });
        dispatch(setQuoteStatus({ msgId, status: QUOTE_STATUS.FAILED }));
        return;
    }
    // The server answers a missing message and a stranger's request alike, with no row.
    const quote = response.data;
    const hasContent = Object.keys(quote?.replyMsgContent || {}).length > 0;
    dispatch(
        hasContent
            ? setQuoteStatus({ msgId, status: QUOTE_STATUS.READY, data: quote })
            : setQuoteStatus({ msgId, status: QUOTE_STATUS.UNAVAILABLE }),
    );
};

export default replySlice.reducer;
